import React from "react";
import { MdAdd, MdClose, MdCheckCircle } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import type {
  QuizQuestionType,
  QuizQuestionOption,
} from "services/instructorQuizQuestionsService";

type Props = {
  type: QuizQuestionType;
  options: QuizQuestionOption[];
  onChange: (next: QuizQuestionOption[]) => void;
};

export default function QuizOptionsEditor({ type, options, onChange }: Props) {
  const language = useAppLanguage();
  const isArabic = language === "ar";

  React.useEffect(() => {
    if (type === "true_false") {
      const desired: QuizQuestionOption[] = [
        { answer: isArabic ? "صح" : "True", is_correct: options[0]?.is_correct ?? true },
        { answer: isArabic ? "خطأ" : "False", is_correct: options[1]?.is_correct ?? false },
      ];
      const correctCount = desired.filter((o) => o.is_correct).length;
      if (correctCount !== 1) {
        desired[0].is_correct = true;
        desired[1].is_correct = false;
      }
      if (
        options.length !== 2 ||
        options[0]?.answer !== desired[0].answer ||
        options[1]?.answer !== desired[1].answer ||
        options[0]?.is_correct !== desired[0].is_correct ||
        options[1]?.is_correct !== desired[1].is_correct
      ) {
        onChange(desired);
      }
    } else if (options.length < 2) {
      onChange([
        ...options,
        ...Array.from({ length: 2 - options.length }, () => ({ answer: "", is_correct: false })),
      ]);
    }
  }, [type, isArabic]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCorrect = (index: number, value: boolean) => {
    if (type === "multiple_choice") {
      const next = options.map((o, i) => (i === index ? { ...o, is_correct: value } : o));
      onChange(next);
      return;
    }
    const next = options.map((o, i) => ({ ...o, is_correct: i === index ? value : false }));
    onChange(next);
  };

  const setAnswer = (index: number, answer: string) => {
    const next = options.map((o, i) => (i === index ? { ...o, answer } : o));
    onChange(next);
  };

  const addRow = () => onChange([...options, { answer: "", is_correct: false }]);
  const removeRow = (index: number) => {
    if (options.length <= 2) return;
    onChange(options.filter((_, i) => i !== index));
  };

  const correctCount = options.filter((o) => o.is_correct).length;
  const isMulti = type === "multiple_choice";
  const readOnlyAnswers = type === "true_false";
  const rule =
    type === "true_false"
      ? isArabic
        ? "اختر الإجابة الصحيحة (واحدة فقط)."
        : "Pick exactly one correct answer."
      : type === "single_choice"
      ? isArabic
        ? "اختر إجابة صحيحة واحدة. على الأقل خياران."
        : "Pick exactly one correct. Minimum two options."
      : isArabic
      ? "اختر إجابة صحيحة واحدة على الأقل."
      : "Pick at least one correct answer.";

  const warning =
    (type === "single_choice" && correctCount !== 1) ||
    (type === "multiple_choice" && correctCount < 1) ||
    options.some((o) => !o.answer.trim());

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">
          {isArabic ? "الخيارات" : "Options"}
        </p>
        <span className="text-[11px] text-gray-400">{rule}</span>
      </div>
      <ul className="flex flex-col gap-2">
        {options.map((opt, i) => (
          <li
            key={i}
            className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 transition-colors ${
              opt.is_correct
                ? "border-emerald-300 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10"
                : "border-gray-200 bg-white dark:border-white/10 dark:bg-navy-900"
            }`}
          >
            <button
              type="button"
              onClick={() => setCorrect(i, !opt.is_correct)}
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-${
                isMulti ? "md" : "full"
              } border transition-colors ${
                opt.is_correct
                  ? "border-emerald-500 bg-emerald-500 text-white"
                  : "border-gray-300 bg-white text-transparent hover:border-emerald-400 dark:border-white/20 dark:bg-navy-800"
              }`}
              aria-label={isArabic ? "علّم كصحيح" : "Mark correct"}
              title={isArabic ? "علّم كصحيح" : "Mark correct"}
            >
              <MdCheckCircle className="h-4 w-4" />
            </button>
            <input
              value={opt.answer}
              onChange={(e) => setAnswer(i, e.target.value)}
              readOnly={readOnlyAnswers}
              placeholder={isArabic ? `الخيار ${i + 1}` : `Option ${i + 1}`}
              className="flex-1 bg-transparent text-sm text-gray-800 outline-none dark:text-white"
            />
            {!readOnlyAnswers && options.length > 2 && (
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10"
                aria-label={isArabic ? "حذف" : "Remove"}
              >
                <MdClose className="h-4 w-4" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {!readOnlyAnswers && (
        <button
          type="button"
          onClick={addRow}
          className="inline-flex w-fit items-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-1 text-xs font-semibold text-gray-600 hover:border-brand-400 hover:text-brand-500 dark:border-white/20 dark:text-gray-200"
        >
          <MdAdd className="h-4 w-4" />
          {isArabic ? "أضف خيارًا" : "Add option"}
        </button>
      )}
      {warning && (
        <p className="text-[11px] text-amber-600 dark:text-amber-300">
          {isArabic
            ? "تأكد من ملء جميع الخيارات واختيار عدد الإجابات الصحيحة المطلوب."
            : "Fill all options and ensure the correct count matches the rule above."}
        </p>
      )}
    </div>
  );
}
